import WasmController from "react-lib/frameworks/WasmController";

import moment from "moment";

// APIs
// CORE
import PackageList from "issara-sdk/apis/PackageList_core";
import PackageDetail from "issara-sdk/apis/PackageDetail_core";
import PackageOrderList from "issara-sdk/apis/PackageOrderList_core";
import PackageOrderDetail from "issara-sdk/apis/PackageOrderDetail_core";
// BIL
import InvoiceItemList from "issara-sdk/apis/InvoiceItemList_apps_BIL";

// Serializer
import PackageOrderSerializerI from "issara-sdk/types/PackageOrderSerializer_core";
import PackageOrderSerializerForDetailI from "issara-sdk/types/PackageOrderSerializerForDetail_core";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";
import { PackageDetailType, UsageLimitType } from "./SettingPackage";

// Utils
import { beToAd } from "react-lib/utils/dateUtils";
import { HandleSearchPurchaseHistory } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";

export type Props = {};

export type State = Partial<{
  // sequence
  PackageOrderSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    filterPackage: Partial<FilterPackageType>;
    filterPurchaseList: Partial<FilterPurchaseListType>;
    filterPurchaseHistory: Partial<FilterPurchaseHistoryType>;
    filterAppointmentHistory: Partial<FilterAppointmentHistoryType>;
    packageList: PackageDetailType[];
    packageTotal: number;
    packageActivePage: number;
    purchaseList: PackageOrderSerializer[];
    purchaseHistoryList: PackageOrderSerializer[];
    appointmentHistoryList: AppointmentLogType[];
    packageInfo: PackageDetailType;
    selectedPackageOrder: PackageOrderSerializerForDetail;
    // Payment
    openCardPaying: boolean;
    invoiceItemByItems: any[];
  }> | null;
}>;

type Picked = Partial<
  Pick<
    MainState,
    | "buttonLoadCheck"
    | "errorMessage"
    | "django"
    | "searchedItemListWithKey"
    | "successMessage"
    | "selectedPatient"
    | "purchaseOrderList"
  >
>;

export type PackageOrderSerializerForDetail = {
  // extra เพิ่มมาจาก detail เพิ่ม ดู usage_limit
  usage_limit?: UsageLimitType;
} & Omit<PackageOrderSerializerForDetailI, "status" | "usage_status_label" | "usage_status"> &
  Pick<PackageOrderSerializer, "status" | "usage_status_label" | "usage_status">;

export type FilterPackageType = {
  isServiceType: boolean;
  serviceType: number | null;
  isPackageType: boolean;
  packageType: number | null;
  isProgramName: boolean;
  programId: number | null;
  programName: number | null;
  isGender: boolean;
  gender: "M" | "F" | "";
  isAgeRange: boolean;
  ageStart: string | number;
  ageEnd: string | number;
  ageRangeError: boolean;
};

type FilterPurchaseListType = {
  product: number | null;
  serviceType: number | null;
  productType: number | null;
  productStatus: "ACTIVE" | "INACTIVE" | "";
};

type FilterPurchaseHistoryType = {
  product: number | null;
  payment: number | null;
  usage: string[] | null;
  division: number | null;
  serviceType: number | null;
  packageType: number | null;
};

type FilterAppointmentHistoryType = {
  package: number | null;
  serviceType: number | null;
  product: number | null;
  startDate: string;
  endDate: string;
  status: "APPOINTMENT" | "COMPLETED";
};

export type PackageOrderSerializer = {
  inactive: boolean;
  product_code: string;
  product_name: string;
  package_type: number;
  package_type_name: string;
  package_service_type: number;
  package_service_type_name: string;
  package_start_date: string;
  package_end_date: string;
  price_normal: string;
  price: string;
  usage_status: keyof typeof USAGE_STATUS;
  usage_status_label: "สมบูรณ์" | "ไม่สมบูรณ์";
  status: keyof typeof ORDER_PAYMENT_STATUS;
} & PackageOrderSerializerI;

export type AppointmentLogType = {
  dt: string;
  usage_datetime: string;
  status: "APPOINTMENT" | "COMPLETED";
  quantity: string;
  package_id: number;
  package_code: string;
  package_name: string;
  product_id: number;
  product_code: string;
  product_name: string;
  package_order_id: number;
  package_order_item_id: number;
  status_label: "นัดหมาย" | "รับบริการแล้ว";
};

export type StatusesFilterType = ("BILLED" | "PAID" | "CANCELED")[];

export type MasterOptionsType = Record<(typeof Masters)[number][0], OptionType[] | undefined>;

export type MasterDataType = Record<(typeof Masters)[number][0], Record<string, any>[]>;

type OptionType = {
  key: number | string;
  value: number | string;
  text: string;
  relate?: string;
};

type SeqState = {
  sequence: "PackageOrder";
  restart?: boolean;
  clear?: boolean;
  card: string;
  divisionId?: number | null;
  statusesFilter?: StatusesFilterType;
  filterByDivision?: boolean;
};

// Handle Action
type ActionType =
  // Search
  | {
      action: "SEARCH";
      card: string;
      statusesFilter?: StatusesFilterType;
      filterByDivision?: boolean;
      activePage?: number;
      offset?: number;
    }
  | { action: "SEARCH_USAGE_HISTORY"; card: string }
  // Action
  | {
      action: "PURCHASE";
      card: string;
      packageId: number;
      quantity?: number;
      restartMedicalFee?: boolean;
    }
  | { action: "DESCRIPTION"; card: string; data: PackageDetailType }
  | {
      action: "SELECT_PACKAGE_ORDER";
      orderId: number;
      index: number;
      card: string;
      isFetchDetail?: boolean; // Get detail เพื่อดู usage_limit เพื่อซ่อนจำนวนนัดหมาย
    }
  | {
      action: "PRINT_PACKAGE_ORDER";
      orderId: number;
      index: number;
      card: string;
    }
  // Method
  | { action: "PAYMENT"; card: string; items: PackageOrderSerializer[] }
  | {
      action: "UPDATE" | "DELETE";
      card: string;
      packageId: number;
      data: Partial<PackageOrderSerializer>;
      orderId: number;
      onSuccess?: Function;
    }
  | {
      action: "CANCEL_PACKAGE";
      data: {
        reason: string;
        packageId: number;
        orderId: number;
        quantity: string;
      };
      statusesFilter?: StatusesFilterType;
      card: string;
    };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => any;

type CustomExtract<T, U> = T extends T ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State = {
  // sequence
  PackageOrderSequence: {
    sequenceIndex: null,
  },
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
  device?: number;
};

export const DataInitial = {};

const Masters = [
  ["packageType", {}],
  ["packageServiceType", {}],
  ["division", {}],
  ["bilStation", {}],
  ["cancelPackageOrder", {}],
] as const;

export const BUTTON_ACTIONS = {
  search: "SEARCH",
  purchase: "PURCHASE",
  desc: "DESCRIPTION",
  delete: "DELETE",
  payment: "PAYMENT",
};

export const USAGE_STATUS = {
  COMPLETED: "COMPLETED",
  INCOMPLETED: "INCOMPLETED",
};

export const ORDER_PAYMENT_STATUS = {
  BILLED: "BILLED",
  PAID: "PAID",
};

export const PACKAGE_SEARCH_PH = "Package_PH";

export const PACKAGE_SEARCH_SP = "Package_SP";

export const PACKAGE_SEARCH_PL = "Package_PL";

export const PACKAGE_SEARCH_AUH = `Package_AUH`;

export const PRODUCT_SEARCH_AUH = `ProductAll_AUH`;

export const DEFAULT_LIST_LIMIT = 10; // Limit สำหรับ Pagination

type Handler<P = any, R = any> = (
  controller: WasmController<State & Picked, Event, Data, Props>,
  params: P,
) => R;

/* ------------------------------------------------------ */

/*                          START                         */

/* ------------------------------------------------------ */
export const GetMaster: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  } as any);

  controller.setState(
    {
      PackageOrderSequence: {
        ...state.PackageOrderSequence,
        sequenceIndex: "Action",
        packageList: [],
        packageTotal: 0,
        packageActivePage: 1,
        purchaseList: [],
        purchaseHistoryList: [],
        filterPurchaseHistory: {
          division: params.divisionId,
        },
        appointmentHistoryList: [],
      },
    },
    () => {
      Action?.(controller, { ...params, action: "SEARCH" });
    },
  );
};

/* ------------------------------------------------------ */

/*                      Handle Action                     */

/* ------------------------------------------------------ */
export const Action: Handler<ActionType> = async (controller, params) => {
  if (params.action === "SEARCH") {
    HandleSearchPackage(controller, params);
  } else if (params.action === "PURCHASE") {
    HandleBuyPackage(controller, params);
  } else if (params.action === "DESCRIPTION") {
    HandleDescription(controller, params);
  }
};

/* ------------------------------------------------------ */

/*                  Handle SearchPackage                  */

/* ------------------------------------------------------ */

const HandleSearchPackage: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.card}_${params.action}`]: "LOADING",
    },
  });

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_LIST_LIMIT;

  const [list, packageData] = await GetPackageList(controller, {
    ...params,
    offset,
  });

  controller.setState({
    PackageOrderSequence: {
      ...state.PackageOrderSequence,
      packageList: list,
      packageTotal: packageData?.total || 0,
      packageActivePage: activePage,
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.card}_${params.action}`]: "SUCCESS",
    },
  });
};

const HandleBuyPackage: Handler<Params<"PURCHASE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.card}_${params.action}_${params.packageId}`]: "LOADING",
    },
  });

  const orderList: any[] = await GetPackageOrderList(controller, {});

  const order = orderList.find((item) => item.product === params.packageId);

  const [_, error] = await CreateUpdatePackageOrder(controller, {
    ...params,
    ...(order && { orderId: order.id }),
    data: {
      quantity: order ? Number(order.quantity) + (params.quantity || 1) : params.quantity || 1,
    },
  });

  if (error) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}_${params.packageId}`]: null,
      },
      errorMessage: {
        ...state.errorMessage,
        ...(error.__error_type__ === "BILLING_LOCKED"
          ? { ModLockExpense: [error] }
          : { [params.card]: error }),
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}_${params.packageId}`]: null,
      },
      successMessage: {
        ...state.successMessage,
        [params.card]: "เพิ่มรายการไปยัง Purchase list แล้ว",
      },
    });

    HandleSearchPurchaseHistory(controller, {
      ...params,
      action: "SEARCH",
    });

    HandleSearchPackage(controller, params);

    controller.handleEvent({
      message: "HandleGetPurchaseOrderList",
      params: {
        restartMedicalFee: params.restartMedicalFee,
      },
    } as any);
  }
};

const HandleDescription: Handler<Params<"DESCRIPTION">> = async (controller, params) => {
  const state = controller.getState();

  const btnKey = `${params.card}_${params.action}_${params.data.id}`;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "LOADING" },
  });

  const [result] = await PackageDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.data.id,
  });

  controller.setState({
    PackageOrderSequence: {
      ...state.PackageOrderSequence,
      packageInfo: result ? { ...result, price: params.data.price } : {},
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: "" },
  });
};

/* ------------------------------------------------------ */

/*                           API                          */

/* ------------------------------------------------------ */
const CreateUpdatePackageOrder: Handler<{
  packageId: number;
  data: Record<string, any>;
  orderId?: number;
}> = async (controller, params) => {
  const state = controller.getState();

  const api = params.orderId ? PackageOrderDetail.update : PackageOrderList.create;

  return api({
    apiToken: controller.apiToken,
    pk: params.orderId,
    data: {
      patient: state.selectedPatient?.id,
      product: params.packageId,
      ...params.data,
    },
    extra: { division: controller.data.division },
  });
};

const GetPackageOrderList: Handler<{ isFilter?: boolean }> = async (controller, params) => {
  const state = controller.getState();

  const filter = state.PackageOrderSequence?.filterPurchaseList || {};
  const packageList: any[] = state.searchedItemListWithKey?.[PACKAGE_SEARCH_PL] || [];
  const product = packageList.find((item) => item.id === filter.product);

  // ต้องระบุ patient id
  if (!state.selectedPatient?.id) {
    return [];
  }

  const code = product?.code;
  let canPurchase: boolean | undefined = undefined;

  if (filter.productStatus === "ACTIVE") {
    canPurchase = true;
  } else if (filter.productStatus === "INACTIVE") {
    canPurchase = false;
  }

  const result = await PackageOrderList.list({
    apiToken: controller.apiToken,
    params: {
      patient: state.selectedPatient?.id,
      statuses: "BILLED",
      active: true,
      ...(params.isFilter
        ? {
            can_purchase: canPurchase,
            package_service_type: filter.serviceType || undefined,
            package_type: filter.productType || undefined,
            keyword: code,
          }
        : {}),
    },
  });

  const items: any[] = (result?.[0]?.items || []).filter(
    (item: any) =>
      // active and ยังไม่ชำระ and code exact match
      item.active && !item.paid_at && (code ? item.product_code === code : true),
  );

  return items;
};

const GetPackageList: Handler<
  { filterByDivision?: boolean; offset?: number },
  Promise<[PackageDetailType[], any]>
> = async (controller, params) => {
  const state = controller.getState();

  const packageOrder = state.PackageOrderSequence || {};
  const filter = packageOrder.filterPackage || {};
  const packageList: any[] = state.searchedItemListWithKey?.[PACKAGE_SEARCH_SP] || [];

  const formatParams = (isKey: keyof FilterPackageType, value: keyof FilterPackageType) => {
    return (filter[isKey] || undefined) && filter[value];
  };

  const code = filter.isProgramName
    ? packageList.find((item) => item.id === filter.programId)?.code
    : undefined;

  const result = await PackageList.list({
    apiToken: controller.apiToken,
    params: {
      active: true,
      package_service_type: formatParams("isServiceType", "serviceType"),
      package_type: formatParams("isPackageType", "packageType"),
      keyword: code || filter.programName,
      gender: formatParams("isGender", "gender"),
      age_start: formatParams("isAgeRange", "ageStart"),
      end_start: formatParams("isAgeRange", "ageEnd"),
      patient: state.selectedPatient?.id,
      limit: DEFAULT_LIST_LIMIT,
      offset: params.offset,
      include_affiliate_exclusive: true,
      ...(params?.filterByDivision && { of_division: controller.data.division }),
    },
  });

  const items: any[] = result[0]?.items || [];

  const packageData = items.filter((item) => {
    const start = moment(beToAd(item.start_date));
    const end = item.end_date ? moment(beToAd(item.end_date)) : null;

    // target date/time to check
    const target = moment();

    // code exact match
    const isMatch = code ? item.code === code : true;

    // Check if the date/time is between the range
    const isInRange = end
      ? target.isBetween(start, end, undefined, "[]")
      : target.isSameOrAfter(start);

    return isInRange && isMatch;
  });

  return [packageData, result[0]];
};

export const GetInvoiceItemByItem: Handler = async (controller, params) => {
  const state = controller.getState();

  return await InvoiceItemList.list({
    params: {
      patient: state.selectedPatient?.id,
      pending: true,
      strict_encounter_type: true,
    },
    extra: { device: controller.data.device },
    apiToken: controller.apiToken,
  });
};
